"use client";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useMatches } from "@/hooks/useMatches";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { splitCorrectScoresListToSections, formatCorrectScoresForGrid, getMatchDisplayTime } from "@/utils/matchInfo";
import { LIVE_STATUSES, MATCH_STATUS as STATUS } from "@/contants";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { formatUnixToVNDate } from "@/utils/format";
import viugoTipsAiSparkle from "@/assets/images/viugo/kbv-v16-tips-ai-sparkle.svg";
import SinOtherBetsDropdown from "../SinOtherBetsDropdown/SinOtherBetsDropdown";
import SinKbvMatchMinute from "../SinKbvMatchMinute";
import SinOddsCell from "../SinOddsCell";
import ViuGoTipsAiModal from "../ViuGoTipsAiModal";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function SinMatch({ match, leagueName, index }) {
  const [showOther, setShowOther] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const [openTipsAiModal, setOpenTipsAiModal] = useState(false);
  const [tipsAiInsightData, setTipsAiInsightData] = useState(null);
  const [loadingTipsAiInsight, setLoadingTipsAiInsight] = useState(false);
  const { getMatchOdds, getMatchInsight } = useMatches();

  const { getOdds, getScore } = useLiveDataForMatch(match?.id);
  const liveOdds = getOdds();
  const liveScore = getScore();

  const statusNum = liveScore?.status !== undefined
    ? Number(liveScore.status)
    : Number(match.status);

  const { currentHomeScore, currentAwayScore } = useMatchData(liveScore, match);
  const isLive = LIVE_STATUSES.includes(statusNum);

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const {
    handicapValue,
    homeHandicapOdds,
    awayHandicapOdds,
    homeHandicapTrend,
    awayHandicapTrend,
    handicapHalfValue,
    homeHandicapHalfOdds,
    awayHandicapHalfOdds,
    homeHandicapHalfTrend,
    awayHandicapHalfTrend,
    overUnderValue,
    overOdds,
    underOdds,
    overTrend,
    underTrend,
    overUnderHalfValue,
    overHalfOdds,
    underHalfOdds,
    overHalfTrend,
    underHalfTrend,
    homeEuropeOdds,
    awayEuropeOdds,
    instantDrawOdds,
    homeEuropeTrend,
    awayEuropeTrend,
    drawTrend,
    homeEuropeHalfOdds,
    awayEuropeHalfOdds,
    drawHalfOdds,
    homeEuropeHalfTrend,
    awayEuropeHalfTrend,
    drawHalfTrend,
  } = useMatchOdds(match, filterOdds, companyIdOdds);

  const homeLogo = match?.homeTeam?.logo || match?.homeLogo;
  const awayLogo = match?.awayTeam?.logo || match?.awayLogo;

  const oddsCell = (props) => <SinOddsCell {...props} />;
  const ftHandicap = oddsCell({
    left: homeHandicapOdds,
    mid: handicapValue,
    right: awayHandicapOdds,
    leftTrend: homeHandicapTrend,
    rightTrend: awayHandicapTrend,
    isHandicap: true,
  });
  const ftOu = oddsCell({
    left: overOdds,
    mid: overUnderValue,
    right: underOdds,
    leftTrend: overTrend,
    rightTrend: underTrend,
  });
  const ftEurope = oddsCell({
    left: homeEuropeOdds,
    mid: instantDrawOdds,
    right: awayEuropeOdds,
    leftTrend: homeEuropeTrend,
    midTrend: drawTrend,
    rightTrend: awayEuropeTrend,
    isEurope: true,
  });
  const h1Handicap = oddsCell({
    left: homeHandicapHalfOdds,
    mid: handicapHalfValue,
    right: awayHandicapHalfOdds,
    leftTrend: homeHandicapHalfTrend,
    rightTrend: awayHandicapHalfTrend,
    isHandicap: true,
  });
  const h1Ou = oddsCell({
    left: overHalfOdds,
    mid: overUnderHalfValue,
    right: underHalfOdds,
    leftTrend: overHalfTrend,
    rightTrend: underHalfTrend,
  });
  const h1Europe = oddsCell({
    left: homeEuropeHalfOdds,
    mid: drawHalfOdds,
    right: awayEuropeHalfOdds,
    leftTrend: homeEuropeHalfTrend,
    midTrend: drawHalfTrend,
    rightTrend: awayEuropeHalfTrend,
    isEurope: true,
  });

  const tableFtHandicap = oddsCell({
    tier: "ft",
    left: homeHandicapOdds,
    mid: handicapValue,
    right: awayHandicapOdds,
    leftTrend: homeHandicapTrend,
    rightTrend: awayHandicapTrend,
    isHandicap: true,
  });
  const tableHtHandicap = oddsCell({
    tier: "ht",
    left: homeHandicapHalfOdds,
    mid: handicapHalfValue,
    right: awayHandicapHalfOdds,
    leftTrend: homeHandicapHalfTrend,
    rightTrend: awayHandicapHalfTrend,
    isHandicap: true,
  });
  const tableFtOu = oddsCell({
    tier: "ft",
    left: overOdds,
    mid: overUnderValue,
    right: underOdds,
    leftTrend: overTrend,
    rightTrend: underTrend,
  });
  const tableHtOu = oddsCell({
    tier: "ht",
    left: overHalfOdds,
    mid: overUnderHalfValue,
    right: underHalfOdds,
    leftTrend: overHalfTrend,
    rightTrend: underHalfTrend,
  });
  const tableFtEurope = oddsCell({
    tier: "ft",
    left: homeEuropeOdds,
    mid: instantDrawOdds,
    right: awayEuropeOdds,
    leftTrend: homeEuropeTrend,
    midTrend: drawTrend,
    rightTrend: awayEuropeTrend,
    isEurope: true,
  });
  const tableHtEurope = oddsCell({
    tier: "ht",
    left: homeEuropeHalfOdds,
    mid: drawHalfOdds,
    right: awayEuropeHalfOdds,
    leftTrend: homeEuropeHalfTrend,
    midTrend: drawHalfTrend,
    rightTrend: awayEuropeHalfTrend,
    isEurope: true,
  });

  const isOddRow = index % 2 === 1;
  const rowBgClass = isOddRow ? "kbv-sin-row-odd" : "kbv-sin-row-even";

  const correctScoresListFull = useMemo(
    () =>
      otherOddsData?.correctScoresOdds
        ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds)
        : [],
    [otherOddsData],
  );
  const correctScoresFull = useMemo(
    () => splitCorrectScoresListToSections(correctScoresListFull),
    [correctScoresListFull],
  );
  const totalCornersOdds = useMemo(
    () =>
      Array.isArray(otherOddsData?.totalCornersOdds)
        ? otherOddsData.totalCornersOdds
        : [],
    [otherOddsData],
  );
  const hasOtherData = useMemo(
    () =>
      (otherOddsData?.goalOddEvenOdds?.length > 0) ||
      (correctScoresListFull.length > 0) ||
      (totalCornersOdds.length > 0),
    [otherOddsData, correctScoresListFull.length, totalCornersOdds.length],
  );

  const handleOtherClick = (e) => {
    e.stopPropagation();
    setShowOther((prev) => !prev);
    if (!showOther && !otherOddsData && match?.id) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) setOtherOddsData(data.data);
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const handleOpenTipsAiModal = async (e) => {
    e?.stopPropagation?.();
    setOpenTipsAiModal(true);
    if (tipsAiInsightData || loadingTipsAiInsight || !match?.id) return;

    setLoadingTipsAiInsight(true);
    try {
      const insightResponse = await getMatchInsight(match.id);
      if (insightResponse?.success && insightResponse?.data) {
        setTipsAiInsightData(insightResponse.data);
      }
    } finally {
      setLoadingTipsAiInsight(false);
    }
  };

  const actionButtons = (size = "desktop") => {
    const isMobile = size === "mobile";
    return (
      <div
        className={`kbv-sin-action-col flex min-w-0 ${isMobile ? "kbv-sin-action-col--mobile flex-row gap-2" : "flex-col items-stretch justify-center gap-2"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleOpenTipsAiModal}
          className={`kbv-sin-btn-tips-ai flex min-w-0 cursor-pointer items-center justify-center gap-1.5 ${isMobile ? "h-9 w-1/2 text-[12px]" : "h-[34px] w-full text-[12px]"}`}
        >
          <img
            src={viugoTipsAiSparkle}
            alt=""
            width={16}
            height={16}
            className="h-4 w-4 shrink-0"
            aria-hidden
          />
          Tips AI
        </button>
        <button
          type="button"
          onClick={handleOtherClick}
          className={`kbv-sin-btn-other-bets flex min-w-0 cursor-pointer items-center justify-center ${isMobile ? "h-9 w-1/2 text-[12px]" : "h-[34px] w-full text-[12px]"}`}
        >
          Cược khác
        </button>
      </div>
    );
  };

  const mobileTeamSide = (logo, name, align) => (
    <div
      className={`kbv-sin-mobile-team flex min-w-0 items-center gap-1.5 ${align === "end" ? "justify-end" : "justify-start"}`}
    >
      {align === "end" ? (
        <>
          <span className="min-w-0 truncate text-right text-[12px] font-medium leading-tight text-[var(--theme-text)]">
            {name}
          </span>
          <img className="h-5 w-5 shrink-0 object-contain" src={logo || ""} alt={name} />
        </>
      ) : (
        <>
          <img className="h-5 w-5 shrink-0 object-contain" src={logo || ""} alt={name} />
          <span className="min-w-0 truncate text-left text-[12px] font-medium leading-tight text-[var(--theme-text)]">
            {name}
          </span>
        </>
      )}
    </div>
  );

  const mobileTeamsRow = (
    <div className="kbv-sin-mobile-teams grid w-full min-w-0 grid-cols-[1fr_auto_1fr] items-center gap-x-2 px-1">
      {mobileTeamSide(homeLogo, match?.homeName, "end")}
      <div className="flex w-10 shrink-0 items-center justify-center">
        {isLive && currentHomeScore !== null && currentAwayScore !== null ? (
          <span className="whitespace-nowrap text-[15px] font-bold text-[var(--color-up)]">
            {currentHomeScore} - {currentAwayScore}
          </span>
        ) : (
          <span className="text-[11px] font-medium text-[var(--theme-text-muted)]">vs</span>
        )}
      </div>
      {mobileTeamSide(awayLogo, match?.awayName, "start")}
    </div>
  );

  const teamLine = (logo, name) => (
    <div className="flex min-w-0 items-center gap-2">
      <img className="h-5 w-5 shrink-0 object-contain md:h-6 md:w-6" src={logo || ""} alt={name} />
      <span className="min-w-0 truncate text-[13px] font-medium leading-tight text-[var(--theme-text)] md:text-[14px]">
        {name}
      </span>
    </div>
  );

  return (
    <>
      <div
        className={`kbv-sin-table-row kbv-sin-table-grid kbv-sin-match-row box-border hidden w-full cursor-pointer px-3 py-2.5 transition-colors md:grid ${rowBgClass} ${isLive ? "kbv-sin-card--live" : ""}`}
      >
        <div className="flex justify-center">
          <SinKbvMatchMinute
            matchTime={match?.matchTime}
            status={statusNum}
            liveScore={liveScore}
            halfStartTime={match?.halfStartTime}
            className={`shrink-0 text-[14px] ${isLive ? "text-[var(--color-up)]" : ""}`}
            showDate
            stacked
          />
        </div>

        <div className="flex min-w-0 items-center gap-2 py-0.5">
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
            {teamLine(homeLogo, match?.homeName)}
            {teamLine(awayLogo, match?.awayName)}
          </div>
          {isLive && currentHomeScore !== null && currentAwayScore !== null ? (
            <div className="flex shrink-0 flex-col items-center justify-center gap-1 tabular-nums text-[15px] font-bold leading-none text-[var(--color-up)]">
              <span>{currentHomeScore}</span>
              <span>{currentAwayScore}</span>
            </div>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-col items-center justify-center gap-1">
          {tableFtHandicap}
          {tableHtHandicap}
        </div>

        <div className="flex min-w-0 flex-col items-center justify-center gap-1">
          {tableFtOu}
          {tableHtOu}
        </div>

        <div className="flex min-w-0 flex-col items-center justify-center gap-1">
          {tableFtEurope}
          {tableHtEurope}
        </div>

        {actionButtons("desktop")}
      </div>

      <div
        className={`kbv-sin-card kbv-sin-match-row kbv-sin-hover flex cursor-pointer flex-col gap-y-2 overflow-hidden py-2 md:hidden ${rowBgClass} ${isLive ? "kbv-sin-card--live" : ""}`}
      >
        <SinKbvMatchMinute
          matchTime={match?.matchTime}
          status={statusNum}
          liveScore={liveScore}
          halfStartTime={match?.halfStartTime}
          className={`flex w-full flex-row items-center justify-center gap-x-1 text-[13px] ${isLive ? "text-[var(--color-up)]" : "text-[var(--theme-text-muted)]"}`}
          showDate
        />

        {mobileTeamsRow}

        <div className="kbv-sin-mobile-odds-body flex w-full flex-col gap-y-2">
          <div className="kbv-sin-mobile-odds-grid">
            <span className="kbv-sin-mobile-odds-grid__ft flex flex-col items-center justify-start pt-0 text-[10px] font-normal text-[var(--theme-text-muted)] opacity-50">
              <span className="mb-1 flex h-8 items-center justify-center">FT</span>
              <span className="my-0.5 h-px w-full bg-[var(--theme-text-muted)] opacity-10" />
              <span className="mt-1 flex h-8 items-center justify-center">HT</span>
            </span>
            <span className="kbv-sin-mobile-odds-grid__col flex min-w-0 flex-col gap-y-1">
              {ftHandicap}
              <span className="my-0.5 h-px w-full bg-[var(--theme-text-muted)] opacity-10" />
              {h1Handicap}
            </span>
            <span className="kbv-sin-mobile-odds-grid__col flex min-w-0 flex-col gap-y-1">
              {ftOu}
              <span className="my-0.5 h-px w-full bg-[var(--theme-text-muted)] opacity-10" />
              {h1Ou}
            </span>
            <span className="kbv-sin-mobile-odds-grid__col flex min-w-0 flex-col gap-y-1">
              {ftEurope}
              <span className="my-0.5 h-px w-full bg-[var(--theme-text-muted)] opacity-10" />
              {h1Europe}
            </span>
          </div>

          <div className="px-1">{actionButtons("mobile")}</div>
        </div>
      </div>

      {showOther && (
        <SinOtherBetsDropdown
          loadingOtherOdds={loadingOtherOdds}
          otherOddsData={otherOddsData}
          hasOtherData={hasOtherData}
          correctScoresListFull={correctScoresListFull}
          correctScoresFull={correctScoresFull}
          totalCornersOdds={totalCornersOdds}
          odds={{
            ...filterOdds,
            homeEuropeOdds,
            awayEuropeOdds,
            instantDrawOdds,
            homeEuropeTrend,
            awayEuropeTrend,
            drawTrend,
            homeHandicapHalfOdds,
            awayHandicapHalfOdds,
            homeHandicapHalfTrend,
            awayHandicapHalfTrend,
          }}
          showOther={showOther}
        />
      )}

      {openTipsAiModal &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[99999998] bg-black/50"
            style={{ backdropFilter: "blur(4px)" }}
            onClick={() => setOpenTipsAiModal(false)}
            aria-hidden="true"
          />,
          document.body,
        )}

      <ViuGoTipsAiModal
        open={openTipsAiModal}
        onClose={() => setOpenTipsAiModal(false)}
        match={match}
        matchInfo={{
          ...formatUnixToVNDate(match?.matchTime),
          isLive,
        }}
        statsInfo={{
          currentHomeScore,
          currentAwayScore,
          homeYellow: null,
          homeRed: null,
          awayYellow: null,
          awayRed: null,
          currentHomeCorner: null,
          currentAwayCorner: null,
        }}
        oddsInfo={{
          handicapValue,
          homeHandicapOdds,
          awayHandicapOdds,
          overUnderValue,
          overOdds,
          underOdds,
          homeEuropeOdds,
          awayEuropeOdds,
          instantDrawOdds,
          handicapHalfValue,
          homeHandicapHalfOdds,
          awayHandicapHalfOdds,
        }}
        leagueName={match?.league?.name || match?.leagueName || leagueName}
        leagueLogo={match?.league?.logo || match?.leagueLogo || ""}
        homeName={match?.homeName}
        homeLogo={homeLogo || ""}
        awayName={match?.awayName}
        awayLogo={awayLogo || ""}
        matchDate={formatUnixToVNDate(match?.matchTime)?.date}
        matchTime={formatUnixToVNDate(match?.matchTime)?.gio}
        matchTimestamp={match?.matchTime}
        headerScore={`${currentHomeScore ?? 0} - ${currentAwayScore ?? 0}`}
        liveScoreToday={liveScore}
        matchStatusNum={statusNum}
        matchTimeUnix={match?.matchTime}
        halfStartTime={match?.halfStartTime}
        isMatchLive={isLive}
        statusLabel={
          !isLive
            ? "Sắp diễn ra"
            : (() => {
                const dt = getMatchDisplayTime(
                  liveScore,
                  statusNum,
                  match?.matchTime,
                  match?.halfStartTime,
                );
                if (dt?.type === "minute") {
                  return dt.halfLabel && dt.halfLabel !== "--" ? dt.halfLabel : "Trực tiếp";
                }
                if (dt?.type === "var") return "VAR";
                return dt?.content ?? "Trực tiếp";
              })()
        }
        insightData={tipsAiInsightData}
        insightLoading={loadingTipsAiInsight}
      />
    </>
  );
}
